import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, CircularProgress, Card, CardContent, Avatar } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import PageHeader1 from '../../components/common/PageHeader1';
import { fetchPoojaDashboardStats } from '../../Redux/Reducers/PoojaDashboardReducer';

function PoojaDashboard() {
    const dispatch = useDispatch();
    const { stats, loading } = useSelector((state) => state.PoojaDashboardReducer || { stats: null, loading: false });

    // Assuming the reducer is registered as PoojaDashboardReducer in combineReducers
    const dashboardState = useSelector((state) => state.PoojaDashboardReducer);
    const poojaStats = dashboardState?.stats;
    const poojaLoading = dashboardState?.loading;

    useEffect(() => {
        dispatch(fetchPoojaDashboardStats());
    }, [dispatch]);

    const cardsData = [
        {
            title: "Total Bookings",
            value: poojaStats?.totalBookings || 0,
            bgClass: "alert-primary",
            iconBgClass: "bg-primary",
            iconClass: "icofont-ticket"
        },
        {
            title: "Total Revenue",
            value: `₹${poojaStats?.totalRevenue?.toLocaleString() || 0}`,
            bgClass: "alert-success",
            iconBgClass: "bg-success",
            iconClass: "icofont-rupee"
        },
        {
            title: "Completion Rate",
            value: `${poojaStats?.completionPercentage || 0}%`,
            bgClass: "alert-info",
            iconBgClass: "bg-info",
            iconClass: "icofont-check-circled"
        },
        {
            title: "Cancellation Rate",
            value: `${poojaStats?.cancellationRate || 0}%`,
            bgClass: "alert-danger",
            iconBgClass: "bg-danger",
            iconClass: "icofont-close-circled"
        }
    ];

    const poojaColumns = [
        {
            field: "image",
            headerName: "Image",
            width: 100,
            renderCell: (params) => {
                const name = params.row?.name;
                const firstLetter = name ? name?.charAt(0)?.toUpperCase() : "TP";
                return (
                    <Avatar 
                        src={params.value || ""} 
                        alt={name || "Pooja"}
                        sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                    >
                        {firstLetter}
                    </Avatar>
                );
            }
        },
        { field: "name", headerName: "Pooja Name", flex: 1 },
        { field: "count", headerName: "Total Bookings", width: 150 },
        { field: "basePrice", headerName: "Base Price", width: 120, renderCell: (params) => `₹${params.value || 0}` },
    ];

    const astroColumns = [
        {
            field: "profileImg",
            headerName: "Profile",
            width: 100,
            renderCell: (params) => {
                const name = params.row?.fullName;
                const firstLetter = name ? name?.charAt(0)?.toUpperCase() : "TA";
                return (
                    <Avatar 
                        src={params.value || ""} 
                        alt={name || "Astrologer"}
                        sx={{ width: 40, height: 40, bgcolor: 'secondary.main' }}
                    >
                        {firstLetter}
                    </Avatar>
                );
            }
        },
        { field: "fullName", headerName: "Astrologer Name", flex: 1 },
        { field: "count", headerName: "Total Bookings", width: 150 },
    ];

    return (
        <div className="body d-flex">
            <div className="container-xxl">
                <PageHeader1 pagetitle="Pooja Dashboard" />

                {poojaLoading ? (
                    <Box display="flex" justifyContent="center" p={5}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <div className="row g-3 mb-4 row-cols-1 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-4">
                            {cardsData.map((d, i) => (
                                <div key={'statuscard' + i} className="col">
                                    <div className={`${d.bgClass} alert mb-0`} style={{ border: 'none', borderRadius: '12px' }}>
                                        <div className="d-flex align-items-center">
                                            <div className={`avatar rounded no-thumbnail ${d.iconBgClass} text-light`}>
                                                <i className={`${d.iconClass} fs-5`}></i>
                                            </div>
                                            <div className="flex-fill ms-3 text-truncate">
                                                <div className="h6 mb-0 fw-bold">{d.title}</div>
                                                <span className="small text-muted fw-bold fs-6">{d.value}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-12">
                                <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none' }}>
                                    <CardContent>
                                        <Typography variant="h6" fontWeight="bold" mb={2}>Top Poojas</Typography>
                                        <Box sx={{ height: 400, width: '100%' }}>
                                            <DataGrid
                                                rows={poojaStats?.topPoojas || []}
                                                columns={poojaColumns}
                                                getRowId={(row) => row._id}
                                                pageSizeOptions={[10, 30, 50]}
                                                initialState={{
                                                    pagination: { paginationModel: { pageSize: 10 } },
                                                }}
                                                disableRowSelectionOnClick
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </div>
                            <div className="col-12">
                                <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none' }}>
                                    <CardContent>
                                        <Typography variant="h6" fontWeight="bold" mb={2}>Top Astrologers</Typography>
                                        <Box sx={{ height: 400, width: '100%' }}>
                                            <DataGrid
                                                rows={poojaStats?.topAstrologers || []}
                                                columns={astroColumns}
                                                getRowId={(row) => row._id}
                                                pageSizeOptions={[10, 30, 50]}
                                                initialState={{
                                                    pagination: { paginationModel: { pageSize: 10 } },
                                                }}
                                                disableRowSelectionOnClick
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default PoojaDashboard;
